import React, { useState, useLayoutEffect } from "react";
import styled from "styled-components";
import Button from '@material-ui/core/Button'
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Fab from '@material-ui/core/Fab';
import Snackbar from '@material-ui/core/Snackbar';

import { isPlural, normalizeSimulation, normalizePathogen } from '../utils/ui.utils'
import { 
  submitSimulation, 
  pushInfection, 
  pushParameters,
  submitQuiz, 
  submissionCheck, 
  submitSurvey, 
  submissionSurveyCheck 
} from '../utils/api.utils'
import { getDuration } from '../utils/date.utils'
import { usePrevious, useIsComponentMounted } from '../utils/hooks.utils'
import { calculatePathogen } from '../utils/equation.utils'

import { AlertType } from '../models'

import { QuizList } from '../components/QuizList'
import { SurveyList } from '../components/SurveyList'
import { QRCodes } from '../components/QRCodes'

import { Alert } from '../components/Alert'

import { GlobalSettings } from '../components/GlobalSettings'
import { PathogenSettings } from '../components/PathogenSettings'
import { URLNotification } from '../components/URLNotification'

import { Flex, PrimaryHeader, NavBackButton, BackIcon } from '../styles/global'

import { bucketSimulation } from '../utils/ui.utils'

// SimulationEditor page takes a single Simulation and allows it to be edited.

interface Props {
  simulation: any
  shouldDisplay: any
  fetchSimulations: any
  initialQuizzes?: any[]
  initialSurveys?: any[]
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      {value === index && (
        <TabContent>{children}</TabContent>
      )}
    </div>
  );
}

export function SimulationEditor({ simulation, shouldDisplay, fetchSimulations, initialQuizzes, initialSurveys }: Props) {
  const isComponentMounted = useIsComponentMounted();
  const [currentSimulation, setSimulation] = useState(normalizeSimulation(simulation));
  const [customPathogen, setCustomPathogen] = useState(simulation.pathogen || {});
  const [tabValue, setTab] = useState<number>(0);
  // const [isValid, setIsValid] = useState<boolean>(true);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertSeverity, setAlertSeverity] = useState<AlertType>(AlertType.SUCCESS);

  const [alertMessage, setAlertMessage] = useState<string>('');
  
  // Track quizzes and surveys state for cloned simulations
  const [currentQuizzes, setCurrentQuizzes] = useState<any[]>(initialQuizzes || []);
  const [currentSurveys, setCurrentSurveys] = useState<any[]>(initialSurveys || []);
  const currentUnitType = currentSimulation.previousType;
  const timeStep = currentSimulation.unit_type;
  const duration = getDuration(currentSimulation.start_date, currentSimulation.end_date, 'minute', currentSimulation.time_zone_IANA)
  const prevDuration = usePrevious(duration);


  const isValid = currentSimulation.name !== ''
    && currentSimulation.code !== ''
    && currentSimulation.end_date
    && currentSimulation.start_date
    && duration > 0
    && currentSimulation.pathogen_id

  useLayoutEffect(() => {
    // if duration has changed, and customPathogen exists, recalculate pathogen
    if (duration !== prevDuration && Object.keys(customPathogen).length !== 0 && !currentSimulation.is_modified) {
      console.log("setCustomPathogen currentUnitType CHANGE = " + timeStep);
      setCustomPathogen(calculatePathogen(currentSimulation, customPathogen, timeStep))
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration]);

  const handleTabChange = (event: any, newValue: number) => {
    setTab(newValue);
  }

  const handleAlertClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setAlertVisible(false);
    setAlertMessage("")
    setAlertSeverity(AlertType.SUCCESS)
  };
  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }
  function prepareSubmission(sim: any) {
    // DEBUG: Uncomment below to debug is_modified persistence issues
    // console.log("DEBUG prepareSubmission: Input sim.is_modified =", sim.is_modified, "type:", typeof sim.is_modified);
    let submission = normalizeSimulation(sim)
    // console.log("DEBUG prepareSubmission: After normalize submission.is_modified =", submission.is_modified, "type:", typeof submission.is_modified);
    if (!!submission.is_modified) {
      // console.log("DEBUG prepareSubmission: is_modified is truthy, attaching pathogen");
      submission = {
        ...submission,
        pathogen: normalizePathogen(customPathogen),
      }
    } else {
      // console.log("DEBUG prepareSubmission: is_modified is falsy, NOT attaching pathogen");
    }
    // console.log("DEBUG prepareSubmission: Final submission =", submission);
    return submission;
  }

  function saveSimulation() {
    const submissionSimulation = prepareSubmission(currentSimulation);

    // send Simulation to network
    submitSimulation(submissionSimulation)
      .then((response: any) => {
        // console.log("DEBUG: Server Response from Save:", response);
        // console.log("DEBUG: Server Response is_modified:", response.is_modified, "pathogen:", response.pathogen);
        
        // After simulation is saved, save any cloned quizzes and surveys
        const savedSimulationCode = response.code || currentSimulation.code;
        
        // Only save quizzes/surveys that haven't been saved yet (no ID or ID is 0/undefined)
        const quizzesToSave = currentQuizzes.filter((quiz: any) => 
          !quiz.quiz_id || quiz.quiz_id === 0
        );
        const surveysToSave = currentSurveys.filter((survey: any) => 
          !survey.survey_id || survey.survey_id === 0
        );

        // Save quizzes and surveys SEQUENTIALLY to preserve order
        // The backend assigns IDs based on insertion order, so we must save one at a time
        const saveQuizzesSequentially = async () => {
          for (const quiz of quizzesToSave) {
            try {
              const quizWithCode = { ...quiz, code: savedSimulationCode };
              const validatedQuiz = submissionCheck(quizWithCode);
              await submitQuiz(validatedQuiz);
            } catch (e) {
              console.error('Error saving cloned quiz:', e);
            }
          }
        };

        const saveSurveysSequentially = async () => {
          for (const survey of surveysToSave) {
            try {
              const surveyWithCode = { ...survey, code: savedSimulationCode };
              const validatedSurvey = submissionSurveyCheck(surveyWithCode);
              await submitSurvey(validatedSurvey);
            } catch (e) {
              console.error('Error saving cloned survey:', e);
            }
          }
        };

        // Wait for all quizzes and surveys to save
        return Promise.all([saveQuizzesSequentially(), saveSurveysSequentially()])
          .then(() => {
            if (quizzesToSave.length > 0 || surveysToSave.length > 0) {
              console.log(`Successfully saved ${quizzesToSave.length} quizzes and ${surveysToSave.length} surveys`);
            }
            fetchSimulations();
            shouldDisplay(false);
            
            // check for active or future sim, send push

            // if (currentSimulation.is_active) {
            let bucket = bucketSimulation(currentSimulation)
            if (bucket === 'active' || bucket === 'future') {
              pushParameters(currentSimulation.code).then((response: any) => {
                if (isComponentMounted.current) {
                  // launch snackbar/toast message based on network response
                  setAlertMessage("New Parameters Pushed");
                  setAlertVisible(true);
                }
                return response;
              })
                .catch((err: any) => console.error(err));
            }
            return response;
          });
      }).catch((err: any) => {
        console.error('Error saving simulation:', err);
        console.error('Error details:', err.response?.data);
        console.error('Payload that failed:', submissionSimulation);
      });
  }

  return (
    <EditorWrapper>
      <Snackbar open={alertVisible} autoHideDuration={6000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity={alertSeverity}>
          {alertMessage}
        </Alert>
      </Snackbar>

      <EditorHeader>
        <NavBackButton startIcon={<BackIcon />} onClick={() => shouldDisplay(false)}>
          Back to Simulations
        </NavBackButton>
        <Flex justify="space-between" alignItems="flex-end">
          <Flex direction="column">
            <PrimaryHeader style={{ margin: 0 }}>{currentSimulation.name || 'Untitled Simulation'}</PrimaryHeader>
            <CodeLabel>Sim Code: <span>{currentSimulation.code}</span></CodeLabel>
          </Flex>

          <ActionToolbar>
            {simulation.archived && !simulation.is_active ?
              <ToolbarButton
                variant="outlined"
                disabled={!simulation.id || simulation.is_active}
                onClick={() => {
                  submitSimulation(prepareSubmission({ ...currentSimulation, archived: false }))
                    .then((response: any) => {
                      fetchSimulations()
                      shouldDisplay(false)
                      return response
                    })
                    .catch((err: any) => console.error(err))
                }}
              >
                Un-Archive
              </ToolbarButton>
              :
              <ToolbarButton
                variant="outlined"
                disabled={!simulation.id || simulation.is_active}
                onClick={() => {
                  submitSimulation(prepareSubmission({ ...currentSimulation, archived: true }))
                    .then((response: any) => {
                      fetchSimulations()
                      shouldDisplay(false)
                      return response
                    })
                    .catch((err: any) => console.error(err))
                }}
              >
                Archive
              </ToolbarButton>
            }
            <ToolbarButton
              variant="contained"
              color="primary"
              disabled={!currentSimulation.code || !currentSimulation.is_active}
              onClick={() => {
                pushInfection(currentSimulation.code)
                  .then((response: any) => {
                    setAlertMessage("Infection Pushed")
                    setAlertVisible(true)
                    return response
                  })
                  .catch((err: any) => console.error(err))
              }}
            >
              Push Infection
            </ToolbarButton>
          </ActionToolbar>
        </Flex>
      </EditorHeader>

      <StatusBanner className={simulation.is_active ? 'active' : 'inactive'}>
        <Flex alignItems="center">
          <StatusDot className={simulation.is_active ? 'active' : 'inactive'} />
          <StatusText>
            {simulation.is_active && !simulation.archived ?
              simulation.active_participants ?
                simulation.infected_participants ?
                  `Simulation is active. There are ${simulation.active_participants} participants and ${simulation.infected_participants} ${isPlural(simulation.infected_participants, 'is', 'are')} infected.`
                  :
                  `Simulation is active. There are ${simulation.active_participants} participants.`
                :
                `Simulation is active.`
              :
              simulation.active_participants ?
                simulation.infected_participants ?
                  `Simulation is inactive, and had ${simulation.active_participants} participants and ${simulation.infected_participants} ${isPlural(simulation.infected_participants, 'was', 'were')} infected.`
                  :
                  `Simulation is inactive, and had ${simulation.active_participants} participants.`
                :
                `Simulation is inactive.`
            }
          </StatusText>
        </Flex>
      </StatusBanner>

      <ThemedTabs
        value={tabValue}
        onChange={handleTabChange}
        aria-label="simulation settings categories"
        TabIndicatorProps={{ style: { backgroundColor: '#3f51b5', height: '3px' } }}
      >
        <Tab label="Global Settings" {...a11yProps(0)} />
        <Tab label="Pathogen Settings" {...a11yProps(1)} />
        <Tab label="Quizzes" {...a11yProps(2)} />
        <Tab label="Surveys" {...a11yProps(3)} />
        <Tab label="QR Codes" {...a11yProps(4)} />
        <Tab label="URL Notification" {...a11yProps(5)} />
      </ThemedTabs>

      <TabPanel value={tabValue} index={0}>
        <GlobalSettings
          currentSimulation={currentSimulation}
          setSimulation={setSimulation}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <PathogenSettings
          currentSimulation={currentSimulation}
          setSimulation={setSimulation}
          setCustomPathogen={setCustomPathogen}
          customPathogen={customPathogen}
          timeStep={currentSimulation.unit_type}
          currentUnitType={currentUnitType}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <QuizList
          code={currentSimulation.code}
          initialItems={initialQuizzes}
          onQuizzesUpdate={setCurrentQuizzes}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={3}>
        <SurveyList
          code={currentSimulation.code}
          initialItems={initialSurveys}
          onSurveysUpdate={setCurrentSurveys}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={4}>
        <QRCodes
          id={currentSimulation.id}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={5}>
        <URLNotification
          code={currentSimulation.code}
        />
      </TabPanel>

      {tabValue !== 2 && tabValue !== 3 && tabValue !== 4 && tabValue !== 5 &&
        <FabWrapper
          variant="extended"
          color="primary"
          disabled={!isValid}
          onClick={() => saveSimulation()}
        >
          SAVE SIMULATION
        </FabWrapper>
      }
    </EditorWrapper>
  );
}

const EditorWrapper = styled.div`
  width: 100%;
  margin: 0;
  padding-bottom: 80px;
`;

const EditorHeader = styled.div`
  margin-bottom: 32px;
`;



const CodeLabel = styled.div`
  font-size: 14px;
  color: #718096;
  margin-top: 4px;
  span {
    font-family: 'Roboto Mono', monospace;
    font-weight: 700;
    color: #1a202c;
    background: #edf2f7;
    padding: 2px 8px;
    border-radius: 4px;
    margin-left: 8px;
  }
`;

const ActionToolbar = styled(Flex)`
  gap: 12px;
`;

const ToolbarButton = styled(Button)`
  && {
    height: 44px;
    padding: 0 24px;
    font-weight: 700;
    text-transform: none;
    border-radius: 10px;
    box-shadow: none;
    &.MuiButton-containedPrimary {
      box-shadow: 0 4px 12px rgba(63, 81, 181, 0.2);
    }
  }
`;

const StatusBanner = styled.div`
  padding: 16px 24px;
  border-radius: 12px;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  
  &.active {
    background: #f0fff4;
    border: 1px solid #c6f6d5;
  }
  
  &.inactive {
    background: #f7fafc;
    border: 1px solid #e2e8f0;
  }
`;

const StatusDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 16px;
  position: relative;
  
  &.active {
    background: #48bb78;
    &::after {
      content: '';
      position: absolute;
      top: -4px;
      left: -4px;
      right: -4px;
      bottom: -4px;
      border-radius: 50%;
      background: #48bb78;
      opacity: 0.3;
      animation: pulse 2s infinite;
    }
  }
  
  &.inactive {
    background: #a0aec0;
  }
  
  @keyframes pulse {
    0% { transform: scale(1); opacity: 0.3; }
    70% { transform: scale(2); opacity: 0; }
    100% { transform: scale(1); opacity: 0; }
  }
`;

const StatusText = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #2d3748;
`;

const ThemedTabs = styled((props: any) => <Tabs {...props} />)`
  margin-bottom: 32px;
  border-bottom: 1px solid #e2e8f0;
  .MuiTab-root {
    text-transform: uppercase;
    font-weight: 700;
    font-size: 13px;
    color: #718096;
    min-width: 140px;
    letter-spacing: 0.05em;
    &.Mui-selected {
      color: #3f51b5;
    }
    &:hover {
      background: #f7fafc;
    }
  }
`;

const TabContent = styled.div`
  animation: fadeIn 0.3s ease-out;
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const FabWrapper = styled(Fab)`
  && {
    position: fixed;
    right: 40px;
    bottom: 40px;
    background-color: #3f51b5;
    color: #fff;
    font-weight: 700;
    text-transform: uppercase;
    padding: 0 32px;
    box-shadow: 0 10px 15px -3px rgba(63, 81, 181, 0.4);
    &:hover {
      background-color: #303f9f;
    }
  }
`;
